import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { ApplicationState } from '../../../reducers';
import { AsyncDispatch } from '../../../../types/global';
import { CustomFieldGroup } from '../../../../types/custom_fields';
import {
  fetchAccountCustomFields,
  fetchCustomFieldsBrands,
  updateCustomFieldToBrands,
  setPendingBrandChanges,
  setPendingBrandChangesForAllFields,
  clearPendingChanges,
  fetchAccountCustomFieldGroups,
  fetchAccountCustomFieldUses,
} from '../../../actions/parent/custom_fields/fetch';
import {
  updateCustomFieldGroups,
  updateCustomFieldOrder,
  deleteCustomField,
  deleteCustomFieldGroup,
  fetchEnhancedValidations,
} from '../../../actions/brand/custom_fields/index';
import { fetchBrands } from '../../../actions/parent/brands/fetch';
import { fetchScripts } from '../../../actions/distribution_apisettings';
import { getProcessedCustomFieldGroups } from '../../../selectors/parent/parentCustomFieldsSelector';
import { activeBrands } from '../../../selectors/brand/brandSelector';
import ParentCustomFieldsPage from '../../../components/body/parentSettings/customFields/ParentCustomFieldsPage';
import { useAppDispatch } from '../../../store/ReduxStore';

const ParentCustomFieldsContainer: React.FC = () => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [selectedCustomFieldId, setSelectedCustomFieldId] = useState<number | null>(null);
  const [showGroupDrawer, setShowGroupDrawer] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<number | string | null>(null);
  const [initialGroupOrder, setInitialGroupOrder] = useState<CustomFieldGroup[]>([]);
  const [currentGroupOrder, setCurrentGroupOrder] = useState<CustomFieldGroup[]>([]);
  const [isDirtyGroups, setIsDirtyGroups] = useState(false);
  const [isDirtyFields, setIsDirtyFields] = useState(false);

  const customFields = useSelector(
    (state: ApplicationState) => state.parent.parentCustomFields.customFields
  );
  const groups = useSelector((state: ApplicationState) => state.parent.parentCustomFields.groups);
  const fieldToBrands = useSelector(
    (state: ApplicationState) => state.parent.parentCustomFields.fieldToBrands
  );
  const customFieldUses = useSelector(
    (state: ApplicationState) => state.parent.parentCustomFields.customFieldUses
  );
  const fetchingCustomFields = useSelector(
    (state: ApplicationState) => state.parent.parentCustomFields.fetchingCustomFields
  );
  const fetchingGroups = useSelector(
    (state: ApplicationState) => state.parent.parentCustomFields.fetchingGroups
  );
  const fetchingFieldToBrands = useSelector(
    (state: ApplicationState) => state.parent.parentCustomFields.fetchingFieldToBrands
  );
  const fetchingCustomFieldUses = useSelector(
    (state: ApplicationState) => state.parent.parentCustomFields.fetchingCustomFieldUses
  );
  const updatingCustomFieldToBrands = useSelector(
    (state: ApplicationState) => state.parent.parentCustomFields.updatingCustomFieldToBrands
  );
  const pendingChanges = useSelector(
    (state: ApplicationState) => state.parent.parentCustomFields.pendingChanges
  );
  const brands = useSelector(activeBrands);
  const user = useSelector((state: ApplicationState) => state.user.user);
  const processedGroups = useSelector(getProcessedCustomFieldGroups);
  const deletingCustomField = useSelector(
    (state: ApplicationState) => state.parent.parentCustomFields.deletingCustomField
  );

  useEffect(() => {
    if (user) {
      dispatch(fetchAccountCustomFields());
      dispatch(fetchAccountCustomFieldGroups());
      dispatch(fetchCustomFieldsBrands());
      dispatch(fetchScripts());
      dispatch(fetchAccountCustomFieldUses());
      dispatch(fetchEnhancedValidations());
      if (user.parent_id) {
        dispatch(fetchBrands(user.parent_id));
      }
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (processedGroups && processedGroups.length > 0 && initialGroupOrder.length === 0) {
      setInitialGroupOrder([...processedGroups]);
      setCurrentGroupOrder([...processedGroups]);
    }
  }, [processedGroups, initialGroupOrder.length]);

  const isLoading =
    fetchingCustomFields || fetchingGroups || fetchingFieldToBrands || fetchingCustomFieldUses;
  const hasPendingChanges =
    Object.keys(pendingChanges).length > 0 || isDirtyGroups || isDirtyFields;

  const handleGroupOrderChange = useCallback(
    (reorderedGroups: CustomFieldGroup[]) => {
      // Check if group order changed
      const hasGroupOrderChanged = reorderedGroups.some((group, index) => {
        const originalGroup = initialGroupOrder[index];
        return !originalGroup || group.id !== originalGroup.id;
      });

      let hasFieldOrderChanged = false;
      reorderedGroups.forEach(group => {
        const originalGroup = initialGroupOrder.find(g => g.id === group.id);
        if (originalGroup) {
          if (group.fields.length !== originalGroup.fields.length) {
            hasFieldOrderChanged = true;
          } else {
            group.fields.forEach((field, fieldIndex) => {
              if (originalGroup.fields[fieldIndex]?.id !== field.id) {
                hasFieldOrderChanged = true;
              }
            });
          }
        }
      });

      setIsDirtyGroups(hasGroupOrderChanged);
      setIsDirtyFields(hasFieldOrderChanged);
      setCurrentGroupOrder(reorderedGroups);
    },
    [initialGroupOrder]
  );

  // Brand management handlers
  const handleBrandChange = useCallback(
    (customFieldId: number, brandIds: number[]) => {
      dispatch(setPendingBrandChanges(customFieldId, brandIds));
    },
    [dispatch]
  );

  const handleSaveChanges = async () => {
    try {
      if (isDirtyGroups) {
        const groupsPayload = currentGroupOrder
          .filter(g => g.id !== 'other')
          .map(({ id, name, record_number }) => ({
            id,
            name,
            record_number,
          }));

        await dispatch(updateCustomFieldGroups(groupsPayload));
      }

      if (isDirtyFields) {
        const newOrder: {
          brand_custom_field_id: number;
          record_number: number;
          group_id: number | null;
        }[] = [];

        let globalRecordNumber = 1;
        currentGroupOrder.forEach(group => {
          group.fields.forEach(field => {
            newOrder.push({
              brand_custom_field_id: field.id,
              record_number: globalRecordNumber++,
              group_id: (() => {
                if (group.id === 'other') {
                  return null;
                }
                if (typeof group.id === 'string') {
                  return Number(group.id.replace('g', ''));
                }
                return Number(group.id);
              })(),
            });
          });
        });

        const sortedCustomFields = newOrder.map(f => {
          const field = customFields.find(field => field.id === f.brand_custom_field_id);
          if (!field) {
            throw new Error(
              t('settings:customFields.fieldNotFoundError', { fieldId: f.brand_custom_field_id })
            );
          }
          return { ...field, record_number: f.record_number, group_id: f.group_id };
        });

        await dispatch(updateCustomFieldOrder(newOrder, sortedCustomFields));
      }

      if (isDirtyGroups || isDirtyFields) {
        await dispatch(fetchAccountCustomFieldGroups());
        await dispatch(fetchAccountCustomFields());
        setInitialGroupOrder([]);
        setCurrentGroupOrder([]);
        setIsDirtyGroups(false);
        setIsDirtyFields(false);
      }

      // Save brand assignment changes if any
      if (Object.keys(pendingChanges).length > 0) {
        // Convert pendingChanges to the API format
        const custom_field_to_brands = Object.entries(pendingChanges).map(
          ([customFieldId, brandIds]) => ({
            custom_field_id: parseInt(customFieldId, 10),
            brand_ids: brandIds,
          })
        );

        customFields.forEach(field => {
          const existingEntry = custom_field_to_brands.find(
            entry => entry.custom_field_id === field.id
          );
          if (!existingEntry) {
            const currentBrandIds =
              fieldToBrands.find(ftb => ftb.custom_field_id === field.id)?.brand_ids || [];
            custom_field_to_brands.push({
              custom_field_id: field.id,
              brand_ids: currentBrandIds,
            });
          }
        });

        await dispatch(updateCustomFieldToBrands({ custom_field_to_brands }));
        dispatch(fetchCustomFieldsBrands());
      }
    } catch (error) {
      /* empty */
    }
  };

  const handleCancelChanges = () => {
    dispatch(clearPendingChanges());
    setCurrentGroupOrder([...initialGroupOrder]);
    setIsDirtyGroups(false);
    setIsDirtyFields(false);
  };

  // Apply all custom fields to all brands handler with batching
  const handleApplyAllToAllBrands = useCallback(() => {
    // Get all available brand IDs
    const allBrandIds = brands.map(brand => brand.id);

    const fieldIds = customFields.map(field => field.id);

    // Batch size set to 50 to balance performance and UI responsiveness.
    // Processing all custom fields at once could cause UI blocking for large datasets.
    // 50 items per batch ensures smooth updates while keeping memory usage reasonable.
    // Combined with requestAnimationFrame scheduling, this prevents browser freezing.
    const BATCH_SIZE = 50;
    let currentBatch: Record<number, number[]> = {};
    let processedCount = 0;

    const processBatch = () => {
      if (processedCount >= fieldIds.length) {
        // All done
        return;
      }

      // Process next batch
      const endIndex = Math.min(processedCount + BATCH_SIZE, fieldIds.length);
      for (let i = processedCount; i < endIndex; i++) {
        currentBatch[fieldIds[i]] = allBrandIds;
      }

      // Dispatch current batch
      dispatch(setPendingBrandChangesForAllFields(currentBatch));
      currentBatch = {};
      processedCount = endIndex;

      // Schedule next batch
      processBatch();
    };

    // Start processing
    processBatch();
  }, [brands, customFields, dispatch]);

  // Custom Field Edit/Add handlers
  const handleShowEditDrawer = (customFieldId?: number) => {
    setSelectedCustomFieldId(customFieldId || null);
    setShowEditDrawer(true);
  };

  const handleCloseEditDrawer = () => {
    setShowEditDrawer(false);
    setSelectedCustomFieldId(null);
  };

  const handleFieldSaved = () => {
    // Refetch custom fields to show updated data
    dispatch(fetchAccountCustomFields());
  };

  // Group management handlers
  const handleShowGroupDrawer = (groupId?: number | string) => {
    setSelectedGroupId(groupId || null);
    setShowGroupDrawer(true);
  };

  const handleCloseGroupDrawer = () => {
    setShowGroupDrawer(false);
    setSelectedGroupId(null);
  };

  const handleAddGroup = async (name: string) => {
    try {
      // Add new group at the beginning of the list with record_number 1
      const updatedGroups = groups.map(g => ({
        ...g,
        record_number: (g.record_number ?? 0) + 1,
      }));

      await dispatch(updateCustomFieldGroups([{ name, record_number: 1 }, ...updatedGroups]));

      // Refetch groups to get the updated data with IDs
      dispatch(fetchAccountCustomFieldGroups());
    } catch (error) {
      /* empty */
    }
  };

  const handleUpdateGroup = async (id: number, name: string) => {
    try {
      const updatedGroups = groups.map(g => (g.id === id ? { ...g, name } : g));
      await dispatch(updateCustomFieldGroups(updatedGroups));
    } catch (error) {
      /* empty */
    }
  };

  const handleDeleteGroup = async (id: number | string) => {
    try {
      await dispatch(deleteCustomFieldGroup(Number(id)));
      // The reducer will handle removing the group from the state
    } catch (error) {
      /* empty */
    }
  };

  const handleDeleteCustomField = async (customFieldId: number) => {
    if (customFieldUses && customFieldUses[customFieldId]) {
      if (user?.admin === 1) {
        message.error(t('settings:customFields.adminDeleteErrorMsg'));
      } else {
        message.error(t('settings:customFields.nonAdminDeleteErrorMsg'));
      }
      return;
    }

    try {
      await dispatch(deleteCustomField(customFieldId));
      // The reducer will handle removing the field from the state
    } catch (error: any) {
      if (error?.response?.status === 422 && error?.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        // Fallback generic error message for other error types
        message.error(t('settings:customFields.failedToDelete'));
      }
      // Re-throw the error so the drawer knows deletion failed
      throw error;
    }
  };

  return (
    <ParentCustomFieldsPage
      groups={isDirtyGroups || isDirtyFields ? currentGroupOrder : processedGroups}
      brands={brands}
      loading={isLoading}
      saving={updatingCustomFieldToBrands}
      hasPendingChanges={hasPendingChanges}
      hasPendingOrderChanges={isDirtyGroups || isDirtyFields}
      onBrandChange={handleBrandChange}
      onGroupOrderChange={handleGroupOrderChange}
      onSaveChanges={handleSaveChanges}
      onCancelChanges={handleCancelChanges}
      onAddCustomField={() => handleShowEditDrawer()}
      onEditCustomField={handleShowEditDrawer}
      showEditDrawer={showEditDrawer}
      selectedCustomFieldId={selectedCustomFieldId}
      onCloseEditDrawer={handleCloseEditDrawer}
      onFieldSaved={handleFieldSaved}
      onDeleteCustomField={handleDeleteCustomField}
      deletingCustomField={deletingCustomField}
      onAddGroup={() => handleShowGroupDrawer()}
      onEditGroup={handleShowGroupDrawer}
      showGroupDrawer={showGroupDrawer}
      selectedGroupId={selectedGroupId}
      onCloseGroupDrawer={handleCloseGroupDrawer}
      onAddGroupAction={handleAddGroup}
      onUpdateGroupAction={handleUpdateGroup}
      onDeleteGroupAction={handleDeleteGroup}
      onApplyAllToAllBrands={handleApplyAllToAllBrands}
    />
  );
};

export default ParentCustomFieldsContainer;
